import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { SupportedCountry } from '@tvia-ai/shared';
import { useAnalytics } from './useAnalytics';

interface JurisdictionDetectionResult {
  detectedCountry: SupportedCountry | null;
  countryCode: string;
  countryName: string;
  region: string;
  city: string;
  confidence: number;
  clientIP: string;
  supportedCountries: Record<string, SupportedCountry>;
  isSupported: boolean;
  fallbackApplied: boolean;
}

interface UseJurisdictionDetectionReturn {
  detectedJurisdiction: SupportedCountry | null;
  isLoading: boolean;
  error: string | null;
  detectionResult: JurisdictionDetectionResult | null;
  retryDetection: () => void;
  canRetry: boolean;
  confidenceLevel: 'high' | 'medium' | 'low';
}

/**
 * Custom hook for auto-detecting user jurisdiction based on IP geolocation
 */
export const useJurisdictionDetection = (autoDetect: boolean = true): UseJurisdictionDetectionReturn => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  
  const [detectedJurisdiction, setDetectedJurisdiction] = useState<SupportedCountry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectionResult, setDetectionResult] = useState<JurisdictionDetectionResult | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const maxRetries = 2;
  const canRetry = retryCount < maxRetries;

  // Determine confidence level based on the detection result
  const confidenceLevel: 'high' | 'medium' | 'low' = detectionResult 
    ? detectionResult.confidence >= 0.8 ? 'high' 
      : detectionResult.confidence >= 0.5 ? 'medium' 
      : 'low'
    : 'low';

  const detectJurisdiction = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/geolocation/detect-jurisdiction', {
        timeout: 10000, // 10 second timeout
      });

      if (response.data.success) {
        const result: JurisdictionDetectionResult = response.data.data;
        
        setDetectionResult(result);
        setDetectedJurisdiction(result.detectedCountry);
        
        
        // Track successful detection
        trackEvent('JURISDICTION_AUTO_DETECTED', {
          detected_country: result.detectedCountry,
          country_code: result.countryCode,
          confidence: result.confidence,
          is_supported: result.isSupported,
          fallback_applied: result.fallbackApplied,
          client_ip: result.clientIP.substring(0, 8) + 'xxx', // Anonymize IP for analytics
          retry_count: retryCount,
        });

        if (result.fallbackApplied) {
          setError(t('jurisdiction.detection.fallbackWarning', 'Could not detect your location. Using default jurisdiction.'));
        }
      } else {
        throw new Error(response.data.error || 'Detection failed');
      }
    } catch (err: any) {
      // Always default to US on any error - no error message shown to user
      setError(null);
      
      // Use US as fallback jurisdiction on error
      setDetectedJurisdiction('US');
      setDetectionResult({
        detectedCountry: 'US',
        countryCode: 'US',
        countryName: 'United States',
        region: '',
        city: '',
        confidence: 0.1,
        clientIP: 'unknown',
        supportedCountries: {
          'US': 'US',
          'GB': 'UK', 
          'IL': 'IL',
          'AE': 'AE',
          'DE': 'DE',
          'FR': 'FR',
        },
        isSupported: true,
        fallbackApplied: true,
      });

      // Track failed detection
      trackEvent('JURISDICTION_DETECTION_FAILED', {
        error: err.response?.data?.message || err.message || 'Unknown error',
        retry_count: retryCount,
        error_type: err.response?.status ? 'api_error' : 'network_error',
        fallback_country: 'US',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const retryDetection = () => {
    if (canRetry) {
      setRetryCount(prev => prev + 1);
      detectJurisdiction();
    }
  };

  // Auto-detect on mount if enabled
  useEffect(() => {
    if (autoDetect && !detectedJurisdiction && !isLoading) {
      detectJurisdiction();
    }
  }, [autoDetect]);

  return {
    detectedJurisdiction,
    isLoading,
    error,
    detectionResult,
    retryDetection,
    canRetry,
    confidenceLevel,
  };
};

/**
 * Hook for getting supported countries list
 */
export const useSupportedCountries = () => {
  const [countries, setCountries] = useState<Record<string, SupportedCountry>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSupportedCountries = async () => {
      try {
        const response = await axios.get('/api/geolocation/supported-countries');
        
        if (response.data.success) {
          setCountries(response.data.data.countries);
        } else {
          throw new Error(response.data.error || 'Failed to fetch countries');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch supported countries');
        // Fallback to hardcoded list
        setCountries({
          'US': 'US',
          'GB': 'UK',
          'IL': 'IL', 
          'AE': 'AE',
          'DE': 'DE',
          'FR': 'FR',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSupportedCountries();
  }, []);

  return { countries, isLoading, error };
};

export default useJurisdictionDetection;